import { Button, Loader, Badge, Tooltip } from '@mantine/core';
import { IconUpload, IconAlertTriangle, IconCheck } from '@tabler/icons-react';
import { useUploadStats } from '@/lib/upload/use-upload-stats';
import { useUploadModal } from './upload-modal-context';

import type { JSX } from "react";

export function UploadButton(): JSX.Element {
  const { open } = useUploadModal();
  const { total, working, errors, ready } = useUploadStats();

  const hasWorking = working > 0;
  const hasErrors = errors > 0 && working === 0;
  const allDone = total > 0 && working === 0 && errors === 0 && ready > 0;

  let leftSection = <IconUpload size={16} />;
  let rightSection: JSX.Element | undefined;
  let color = 'dark';
  let label = 'Upload';
  let tooltip = 'Mass upload images';

  if (hasWorking) {
    leftSection = <Loader size="xs" color="white" />;
    rightSection = (
      <Badge size="sm" color="blue" variant="filled" circle>
        {working}
      </Badge>
    );
    color = 'dark';
    label = 'Uploading';
    tooltip = `${working} working · ${ready} ready · ${errors} errors`;
  } else if (hasErrors) {
    leftSection = <IconAlertTriangle size={16} />;
    rightSection = (
      <Badge size="sm" color="red" variant="filled" circle>
        {errors}
      </Badge>
    );
    color = 'red';
    label = 'Upload';
    tooltip = `${errors} upload error${errors === 1 ? '' : 's'} — click to review`;
  } else if (allDone) {
    leftSection = <IconCheck size={16} />;
    rightSection = (
      <Badge size="sm" color="teal" variant="filled" circle>
        {ready}
      </Badge>
    );
    color = 'teal';
    label = 'Upload';
    tooltip = `${ready} ready to publish — click to clear`;
  }

  return (
    <Tooltip label={tooltip} withArrow>
      <Button
        variant="filled"
        color={color}
        leftSection={leftSection}
        rightSection={rightSection}
        onClick={open}
        aria-label={tooltip}
      >
        {label}
      </Button>
    </Tooltip>
  );
}
